import { useEffect, useRef } from 'react';
import { Navigate } from '@tanstack/react-router';
import { useToast } from '@mustauto/ui/web';
import { useAuth } from '@/hooks/useAuth';
import { useCan } from '@/hooks/useCan';
import { canOpenRoute } from '@/lib/route-access';

/**
 * Tela que a pessoa não pode abrir nesta oficina (Fase 3): quem chega pela URL
 * — link salvo, histórico do navegador — volta para as OS com um aviso, em vez
 * de ver a tela quebrar no 403 da API. A regra é a do menu (`lib/route-access`).
 */
function RouteGuard({ path, children }: { path: string; children: React.ReactNode }) {
  const { loading } = useAuth();
  const can = useCan();
  const { toast } = useToast();
  const allowed = canOpenRoute(path, can);
  const warned = useRef(false);

  useEffect(() => {
    if (!loading && !allowed && !warned.current) {
      warned.current = true;
      toast({ type: 'info', title: 'Você não tem acesso a esta tela nesta oficina' });
    }
  }, [loading, allowed, toast]);

  if (loading || allowed) return <>{children}</>;
  return <Navigate to="/service-orders" replace />;
}

/** O componente da rota, atrás da guarda da tela. */
export function guarded(path: string, Page: () => React.ReactNode): () => React.ReactNode {
  const Guarded = () => (
    <RouteGuard path={path}>
      <Page />
    </RouteGuard>
  );
  Guarded.displayName = `Guarded(${path})`;
  return Guarded;
}
